import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaEnvelope, FaLock, FaGoogle, FaShieldAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useForceReload } from '../hooks/useForceReload';

const SignInPage = () => {
  // Force page reload when navigating to this page
  useForceReload();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    verificationCode: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const navigate = useNavigate();
  const { login, verifyEmail, resendVerification, pendingVerification } = useAuth();

  // Set background color on mount
  useEffect(() => {
    // Detect iOS for specific fixes
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    if (isIOS) {
      document.documentElement.classList.add('ios');
      document.body.classList.add('ios');
    }
    
    // Add allow-scroll class to enable scrolling
    document.documentElement.classList.add('allow-scroll');
    document.body.classList.add('allow-scroll');
    document.body.style.backgroundColor = '#1E0B38';
    
    // Check if there's a pending verification
    if (pendingVerification) {
      setFormData(prev => ({ ...prev, email: pendingVerification }));
      setShowVerification(true);
    }
    
    return () => {
      // Cleanup on unmount
      if (isIOS) {
        document.documentElement.classList.remove('ios');
        document.body.classList.remove('ios');
      }
      document.documentElement.classList.remove('allow-scroll');
      document.body.classList.remove('allow-scroll');
      document.body.style.backgroundColor = '';
    };
  }, [pendingVerification]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateVerificationCode = () => {
    const newErrors = {};
    
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      newErrors.verificationCode = 'Please enter a valid 6-digit verification code.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      const user = await login(formData.email, formData.password);
      
      // Always redirect to /search regardless of role
      navigate('/search');
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.response?.data?.requiresVerification) {
        // Show verification form if email is not verified
        setShowVerification(true);
        toast.error('Please verify your email before logging in.');
      } else {
        setErrors({
          form: error.response?.data?.error || 'Invalid email or password. Please try again.'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!validateVerificationCode()) return;
    
    setIsVerifying(true);
    try {
      await verifyEmail(formData.email, formData.verificationCode);
      
      toast.success(
        'Email verified successfully! You are now logged in.',
        {
          duration: 5000,
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        }
      );
      
      // Redirect to user page after verification
      navigate('/search');
    } catch (error) {
      console.error('Verification error:', error);
      
      if (error.response?.status === 410) {
        setErrors({
          verificationCode: 'Verification code has expired. Please request a new one.'
        });
      } else {
        setErrors({
          verificationCode: error.response?.data?.error || 'Invalid verification code. Please try again.'
        });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await resendVerification(formData.email);
      toast.success('Verification code sent successfully!');
    } catch (error) {
      console.error('Error resending code:', error);
      toast.error(error.response?.data?.error || 'Failed to resend verification code');
    }
  };

  const handleBackToSignIn = () => {
    setShowVerification(false);
    setFormData(prev => ({ ...prev, verificationCode: '' }));
    setErrors({});
  };

  const handleGoogleSignIn = () => {
    // Redirect to Google OAuth URL
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google`;
  };

  if (showVerification) {
    return (
      <div className="auth-page-container" style={{ background: 'linear-gradient(180deg, #301555 0%, #411C73 50%, #1E0B38 100%)' }}>
        {/* Background decorative elements - fixed position */}
        <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
          <div className="absolute top-[10%] left-[15%] w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-[40%] right-[15%] w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-[10%] left-[35%] w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>
        
        {/* Content container */}
        <div className="mx-auto px-4 py-10 relative z-10">
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-8 space-y-6 border border-white/20">
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Verify Your Email
                </h1>
                <p className="text-gray-300">
                  We've sent a verification code to <span className="font-semibold">{formData.email}</span>
                </p>
              </div>
              
              {/* Form */}
              <form onSubmit={handleVerify} className="space-y-5">
                {errors.verificationCode && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg">
                    {errors.verificationCode}
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">Verification Code</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaShieldAlt className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="verificationCode"
                      placeholder="6-digit code"
                      maxLength={6}
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
                      value={formData.verificationCode}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={isVerifying}
                >
                  {isVerifying ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </span>
                  ) : "Verify Email"}
                </button>
                
                <div className="text-center mt-4 space-y-2">
                  <p className="text-sm text-gray-300">
                    Didn't receive the code?{' '}
                    <button
                      type="button"
                      onClick={handleResendCode}
                      className="text-purple-400 hover:text-purple-300 font-medium"
                    >
                      Resend Code
                    </button>
                  </p>
                  
                  <p className="text-sm text-gray-300">
                    <button
                      type="button"
                      onClick={handleBackToSignIn}
                      className="text-purple-400 hover:text-purple-300 font-medium"
                    >
                      Back to Sign In
                    </button>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-container" style={{ background: 'linear-gradient(180deg, #301555 0%, #411C73 50%, #1E0B38 100%)' }}>
      {/* Background decorative elements - fixed position */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[10%] left-[15%] w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-[40%] right-[15%] w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-[10%] left-[35%] w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>
      
      {/* Content container */}
      <div className="mx-auto px-4 py-10 relative z-10">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-8 space-y-6 border border-white/20">
            {/* Header */}
            <div className="text-center space-y-2">
              <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                Welcome Back
              </h1>
              <p className="text-gray-300">Sign in to your account</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {errors.form && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg">
                  {errors.form}
                </div>
              )}

              {/* Email field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Password field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    name="password"
                    placeholder="••••••••"
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
                {errors.password && (
                  <p className="text-red-400 text-sm mt-1">{errors.password}</p>
                )}
              </div>

              {/* Remember me & Forgot password */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="remember"
                    className="h-4 w-4 rounded border-gray-300 text-purple-500 focus:ring-purple-500/20"
                  />
                  <label htmlFor="remember" className="ml-2 text-gray-300">Remember me</label>
                </div>
                <Link to="/forgot-password" className="text-purple-400 hover:text-purple-300 transition-colors">
                  Forgot password?
                </Link>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : "Sign in"}
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-[#301555] text-gray-400">Or continue with</span>
                </div>
              </div>

              {/* Social login */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 font-medium rounded-lg transition-all"
              >
                <FaGoogle className="h-5 w-5" />
                Google
              </button>

              {/* Sign up link */}
              <p className="text-center text-gray-400">
                Don't have an account?{' '}
                <Link to="/signup" className="text-purple-400 hover:text-purple-300 font-medium transition-colors">
                  Sign up
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage; 